import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  AuthActions,
  AuthField,
  AuthNotice,
  AuthPage,
  PasswordInput,
  SubmitButton,
  authKitTextKeys,
  describedBy,
  showResult,
} from "../../auth/authKit";
import { ChoiceMenu } from "../../ui/ChoiceMenu";
import { ChatIcon } from "../../ui/icons/Icon";
import { register } from "./accountApi";
import { useFocusHeading } from "./focusHeading";

const countries = ["PE", "CO", "MX", "CL", "ES", "US"];
const timezones: [string, string][] = [
  ["America/Lima", "REG_TZ_LIMA"],
  ["America/Bogota", "REG_TZ_BOGOTA"],
  ["America/Mexico_City", "REG_TZ_MEXICO_CITY"],
  ["America/Santiago", "REG_TZ_SANTIAGO"],
  ["Europe/Madrid", "REG_TZ_MADRID"],
  ["America/New_York", "REG_TZ_NEW_YORK"],
];

/** Claves de texto de la pantalla (las que lee Register.razor), las del kit e «Ingresar» para volver al ingreso. */
export const registerTextKeys = [
  ...authKitTextKeys,
  "REG_",
  "LOGIN_SUBMIT",
  "CP_PASSWORD_RULE",
  "COUNTRY_",
  "FEEDBACK_FIRST_REQUIRED",
  "CP_REG_COMPANY_REQUIRED",
  "CP_REG_EMAIL_INVALID",
  "CP_REG_COUNTRY_INVALID",
];

/**
 * Desfase vigente de la zona en horas, con signo y sin cero a la izquierda («+2», «-5», «0»), como Register.razor
 * (TimeZoneInfo.GetUtcOffset y el formato "+0;-0;0"): incluye el horario de verano de hoy.
 */
export function utcOffset(zone: string, now: Date): string {
  const name =
    new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" })
      .formatToParts(now)
      .find((part) => part.type === "timeZoneName")?.value ?? "GMT";
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
  if (!match) return "0";
  const hours = Math.round(Number(match[2]) + Number(match[3]) / 60);
  return hours === 0 ? "0" : `${match[1]}${hours}`;
}

/**
 * Alta self-service (/register, Register.razor) con las piezas del kit: usuario, empresa y membresía de dueño. El
 * servidor valida y orquesta; con éxito la sesión ya quedó emitida y se va al portal. Tras un error se conservan los
 * datos escritos salvo la contraseña; cada error va bajo su campo (C05, sin resumen) y el foco va al primero. País y
 * zona horaria se eligen con el selector del kit (C08: 6 opciones, nunca el desplegable nativo).
 */
export function RegisterPage({ L }: { L: (key: string) => string }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    country: "PE",
    timezone: "America/Lima",
  });
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  const password = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const [now] = useState(() => new Date());
  const title = L("REG_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);
  useFocusHeading(true);
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    let leaving = false;
    try {
      const result = await register({ ...form, password: password.current?.value ?? "" });
      if ((result.status === "signedIn" || result.status === "redirect") && result.next) {
        leaving = true;
        window.location.assign(result.next);
        return;
      }
      const issues = result.fields ?? [];
      if (password.current) password.current.value = "";
      showResult(() => {
        setFields(Object.fromEntries(issues.map((issue) => [issue.field, L(issue.messageKey)])));
        // Con errores de campo, cada uno va bajo su campo: el texto general sería el resumen que C05 no lleva.
        setError(issues.length === 0 && result.messageKey ? L(result.messageKey) : null);
        setAttempt((value) => value + 1);
      }, issues.length > 0);
    } finally {
      if (!leaving) {
        busy.current = false;
        setSending(false);
      }
    }
  };

  const invalid = (field: string) => (fields[field] ? "true" : undefined);
  const menuIcons = {
    chevron: <ChatIcon name="chevron-down" />,
    check: <ChatIcon name="check" />,
  };
  return (
    <AuthPage title={title}>
      <p className="auth-intro">{L("REG_SUB")}</p>
      {error ? (
        <AuthNotice key={attempt} tone="error">
          {error}
        </AuthNotice>
      ) : null}
      <form method="post" onSubmit={(event) => void submit(event)}>
        <div className="tm-fields">
          <AuthField id="firstname" label={L("REG_FIRST")} error={fields.firstname}>
            <input
              id="firstname"
              name="form.FirstName"
              value={form.firstName}
              onChange={set("firstName")}
              autoComplete="given-name"
              aria-invalid={invalid("firstname")}
              aria-describedby={describedBy("firstname", fields.firstname)}
              required
            />
          </AuthField>
          <AuthField id="lastname" label={L("REG_LAST")}>
            <input
              id="lastname"
              autoComplete="family-name"
              name="form.LastName"
              value={form.lastName}
              onChange={set("lastName")}
            />
          </AuthField>
        </div>
        <AuthField id="email" label={L("REG_EMAIL")} error={fields.email}>
          <input
            id="email"
            name="form.Email"
            value={form.email}
            onChange={set("email")}
            type="email"
            autoComplete="email"
            aria-invalid={invalid("email")}
            aria-describedby={describedBy("email", fields.email)}
            required
          />
        </AuthField>
        <AuthField id="password" label={L("REG_PASS")} error={fields.password} help={L("CP_PASSWORD_RULE")}>
          <PasswordInput
            id="password"
            name="form.Password"
            autoComplete="new-password"
            inputRef={password}
            error={fields.password}
            help={L("CP_PASSWORD_RULE")}
            required
            L={L}
          />
        </AuthField>
        <AuthField id="company" label={L("REG_COMPANY")} error={fields.company}>
          <input
            id="company"
            name="form.CompanyName"
            value={form.companyName}
            onChange={set("companyName")}
            autoComplete="organization"
            aria-invalid={invalid("company")}
            aria-describedby={describedBy("company", fields.company)}
            required
          />
        </AuthField>
        <div className="tm-fields">
          <AuthField id="country" label={L("REG_COUNTRY")} error={fields.country}>
            <ChoiceMenu
              id="country"
              label={L("REG_COUNTRY")}
              value={form.country}
              onChange={(country) => setForm((current) => ({ ...current, country }))}
              options={countries.map((code) => ({ value: code, label: L(`COUNTRY_${code}`) }))}
              invalid={!!fields.country}
              describedBy={describedBy("country", fields.country)}
              {...menuIcons}
            />
          </AuthField>
          <AuthField id="timezone" label={L("REG_TZ")}>
            <ChoiceMenu
              id="timezone"
              label={L("REG_TZ")}
              value={form.timezone}
              onChange={(timezone) => setForm((current) => ({ ...current, timezone }))}
              options={timezones.map(([zone, key]) => ({ value: zone, label: `${L(key)} UTC${utcOffset(zone, now)}` }))}
              {...menuIcons}
            />
          </AuthField>
        </div>
        <AuthActions
          secondary={
            <a className="tm-btn secondary" href="/login">
              {L("LOGIN_SUBMIT")}
            </a>
          }
        >
          <SubmitButton sending={sending}>{L("REG_SUBMIT")}</SubmitButton>
        </AuthActions>
      </form>
    </AuthPage>
  );
}
